import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useUserData } from '@/hooks/useUserData'
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, setDoc, addDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { GuestSpot, Artist } from '@/types/models'
import { GuestSpotCard } from '@/components/guestartists/GuestSpotCard'
import { GuestSpotEditModal } from '@/components/guestartists/GuestSpotEditModal'
import { InfoModal } from '@/components/guestartists/InfoModal'
import { Plus, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CitySelector } from '@/components/shared/CitySelector'
import { getCityCoordinates, loadWorldCities, isWorldCitiesLoaded, getCityPopulation } from '@/lib/worldCities'
import { useAnalytics } from '@/hooks/useAnalytics'
import { GuestArtistsMap } from '@/components/guestartists/GuestArtistsMap'
import { GUEST_ARTISTS_LIVE_CITY_NAMES } from '@/lib/guestArtistsLiveCities'
import {
  LOCAL_GUEST_SPOTS_ARCHIVED_COLLECTION,
  LOCAL_GUEST_SPOTS_COLLECTION,
  parseLocalGuestSpotDocument,
} from '@/lib/localGuestSpots'
import {
  fetchPlatformArtistsForGuestSpots,
  resolvePlatformArtistForGuestSpot,
} from '@/lib/guestArtistsPlatformArtists'
import {
  LOCAL_GUEST_SPOT_MANUAL_OVERRIDES_DOC_ID,
  recordLocalGuestSpotManualOverrides,
} from '@/lib/localGuestSpotManualOverrides'

export default function GuestArtistsPage() {
  const { userData, isAdminModeActive } = useUserData()
  const [searchParams, setSearchParams] = useSearchParams()
  const { trackEvent } = useAnalytics()
  
  const [hasInitialized, setHasInitialized] = useState(false)
  const [guestSpots, setGuestSpots] = useState<GuestSpot[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [artistsById, setArtistsById] = useState<Map<string, Artist>>(new Map())
  const [artistsByInstagramHandle, setArtistsByInstagramHandle] = useState<Map<string, Artist>>(new Map())
  const [isLoadingSpots, setIsLoadingSpots] = useState(true)
  const [cities] = useState<string[]>(() => [...GUEST_ARTISTS_LIVE_CITY_NAMES])
  const isLoadingCities = false
  const [isLoadingWorldCities, setIsLoadingWorldCities] = useState(true)
  
  // Admin-specific state - only active if admin mode is enabled
  const isAdmin = isAdminModeActive
  const [editingGuestSpot, setEditingGuestSpot] = useState<GuestSpot | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)

  // Get selected city from URL params or empty string
  const selectedCity = searchParams.get('city') || ''

  // Disable zoom on this page only
  useEffect(() => {
    const viewportMetaTag = document.querySelector('meta[name="viewport"]')
    const originalContent = viewportMetaTag?.getAttribute('content') || ''
    
    if (viewportMetaTag) {
      viewportMetaTag.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover')
    }
    
    return () => {
      if (viewportMetaTag && originalContent) {
        viewportMetaTag.setAttribute('content', originalContent)
      }
    }
  }, [])

  // Load worldcities CSV data
  useEffect(() => {
    const loadCities = async () => {
      try {
        await loadWorldCities()
        console.log('[GuestArtistsPage] World cities loaded')
      } catch (error) {
        console.error('[GuestArtistsPage] Error loading world cities:', error)
      } finally {
        setIsLoadingWorldCities(false)
      }
    }

    if (!isWorldCitiesLoaded()) {
      loadCities()
    } else {
      setIsLoadingWorldCities(false)
    }
  }, [])

  // Handle city change - update URL params
  const handleCityChange = useCallback((cityName: string, isUserInitiated = false) => {
    const newParams = new URLSearchParams(searchParams)
    if (cityName) {
      newParams.set('city', cityName)
    } else {
      newParams.delete('city')
    }
    setSearchParams(newParams, { replace: true })
    if (isUserInitiated && cityName) {
      trackEvent('guest_city_selected', { city: cityName })
    }
  }, [searchParams, setSearchParams, trackEvent])

  // Default city when URL has none (most populous live market)
  useEffect(() => {
    if (isLoadingCities || isLoadingWorldCities || cities.length === 0 || hasInitialized) return

    if (selectedCity && cities.includes(selectedCity)) {
      setHasInitialized(true)
      return
    }

    const sortedByPopulation = [...cities].sort((a, b) => {
      const popA = getCityPopulation(a) || 0
      const popB = getCityPopulation(b) || 0
      return popB - popA
    })
    const defaultCity = sortedByPopulation[0]
    if (defaultCity) {
      handleCityChange(defaultCity)
      setHasInitialized(true)
    }
  }, [cities, isLoadingCities, isLoadingWorldCities, selectedCity, hasInitialized, handleCityChange])

  // Fetch guest spots based on selected city
  useEffect(() => {
    if (!selectedCity) return

    const fetchGuestSpots = async () => {
      setIsLoadingSpots(true)
      setFetchError(null)
      try {
        console.log('[GuestArtistsPage] Fetching local guest spots for city:', selectedCity)
        const spotsRef = collection(db, LOCAL_GUEST_SPOTS_COLLECTION)
        const now = new Date()
        console.log('[GuestArtistsPage] Current date:', now)
        
        const q = query(
          spotsRef,
          where('targetCity', '==', selectedCity),
          where('endDate', '>=', now),
          orderBy('endDate', 'asc')
        )
        
        const snapshot = await getDocs(q)
        console.log('[GuestArtistsPage] Found local guest spots:', snapshot.docs.length)
        const spots: GuestSpot[] = []
        
        snapshot.forEach((spotDocument) => {
          if (
            spotDocument.id === 'metadata' ||
            spotDocument.id === LOCAL_GUEST_SPOT_MANUAL_OVERRIDES_DOC_ID
          ) {
            return
          }

          const spot = parseLocalGuestSpotDocument(spotDocument.id, spotDocument.data())
          spots.push(spot)
        })
        
        setGuestSpots(spots)

        const { artistsById: loadedArtistsById, artistsByInstagramHandle: loadedArtistsByInstagramHandle } =
          await fetchPlatformArtistsForGuestSpots(spots)

        setArtistsById(loadedArtistsById)
        setArtistsByInstagramHandle(loadedArtistsByInstagramHandle)
      } catch (error) {
        console.error('[GuestArtistsPage] Error fetching guest spots:', error)
        setGuestSpots([])
        const errorMessage = error instanceof Error ? error.message : 'Failed to load guest spots'
        setFetchError(errorMessage)
      } finally {
        setIsLoadingSpots(false)
      }
    }

    fetchGuestSpots()
  }, [selectedCity])

  // Get coordinates for the selected city from worldcities.csv
  const selectedCityCoordinates = useMemo(() => {
    if (!selectedCity) return null
    return getCityCoordinates(selectedCity)
  }, [selectedCity])

  // Admin handlers
  const handleEditGuestSpot = (guestSpot: GuestSpot) => {
    setEditingGuestSpot(guestSpot)
    setIsEditModalOpen(true)
  }

  const handleCreateGuestSpot = () => {
    setIsCreateModalOpen(true)
  }

  const handleSaveGuestSpot = async (
    updatedData: Partial<GuestSpot>,
    overriddenFieldNames: string[] = [],
  ) => {
    if (!editingGuestSpot) return

    try {
      const guestSpotRef = doc(db, LOCAL_GUEST_SPOTS_COLLECTION, editingGuestSpot.id)
      await updateDoc(guestSpotRef, updatedData)

      const updatedGuestSpot: GuestSpot = {
        ...editingGuestSpot,
        ...updatedData,
        startDate: updatedData.startDate instanceof Date ? updatedData.startDate : editingGuestSpot.startDate,
        endDate: updatedData.endDate instanceof Date ? updatedData.endDate : editingGuestSpot.endDate,
      }

      await recordLocalGuestSpotManualOverrides({
        guestSpot: updatedGuestSpot,
        overriddenFieldNames,
        updatedByUserId: userData?.uid,
      })

      // Update local state with proper date conversion and field deletion handling
      setGuestSpots(prevSpots =>
        prevSpots.map(spot => {
          if (spot.id !== editingGuestSpot.id) return spot
          
          const updatedSpot = {
            ...spot,
            ...updatedData,
            startDate: updatedData.startDate instanceof Date ? updatedData.startDate : spot.startDate,
            endDate: updatedData.endDate instanceof Date ? updatedData.endDate : spot.endDate,
          }
          
          // Handle deleted fields - if deleteField() was used, remove them from local state
          // deleteField() is a special Firestore sentinel, we need to check for it
          Object.keys(updatedData).forEach(key => {
            const value = (updatedData as any)[key]
            // Check if this is a Firestore deleteField sentinel
            if (value && typeof value === 'object' && '_methodName' in value) {
              delete (updatedSpot as any)[key]
            }
          })
          
          return updatedSpot
        })
      )

      console.log('[GuestArtistsPage] Guest spot updated successfully:', editingGuestSpot.id)
    } catch (error) {
      console.error('[GuestArtistsPage] Error updating guest spot:', error)
      throw error
    }
  }

  const handleCreateNewGuestSpot = async (guestSpotData: Partial<GuestSpot>) => {
    try {
      const guestSpotsRef = collection(db, LOCAL_GUEST_SPOTS_COLLECTION)
      const docRef = await addDoc(guestSpotsRef, guestSpotData)

      // Create the new guest spot object with the generated ID
      const newGuestSpot: GuestSpot = {
        id: docRef.id,
        targetCity: guestSpotData.targetCity!,
        targetCountry: guestSpotData.targetCountry!,
        startDate: guestSpotData.startDate!,
        endDate: guestSpotData.endDate!,
        createdAt: guestSpotData.createdAt!,
        username: guestSpotData.username,
        profileImageUrl: guestSpotData.profileImageUrl,
        homeCity: guestSpotData.homeCity,
        homeCountry: guestSpotData.homeCountry,
        homeLat: guestSpotData.homeLat,
        homeLon: guestSpotData.homeLon,
        targetLat: guestSpotData.targetLat,
        targetLon: guestSpotData.targetLon,
        studioName: guestSpotData.studioName,
      }

      // Add to local state if it matches the current filter
      if (newGuestSpot.targetCity === selectedCity && newGuestSpot.endDate >= new Date()) {
        setGuestSpots(prevSpots => [...prevSpots, newGuestSpot].sort((a, b) => 
          a.endDate.getTime() - b.endDate.getTime()
        ))
      }

      console.log('[GuestArtistsPage] Guest spot created successfully:', docRef.id)
    } catch (error) {
      console.error('[GuestArtistsPage] Error creating guest spot:', error)
      throw error
    }
  }

  const handleDeleteGuestSpot = async (guestSpot: GuestSpot) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to archive this guest spot for ${guestSpot.username || 'this artist'}?`
    )
    
    if (!confirmDelete) return

    try {
      // Create a copy in the archived collection, filtering out undefined values
      const archivedSpotRef = doc(db, LOCAL_GUEST_SPOTS_ARCHIVED_COLLECTION, guestSpot.id)
      
      // Remove undefined values to prevent Firestore errors
      const guestSpotDataToArchive = Object.fromEntries(
        Object.entries({
          ...guestSpot,
          archivedAt: new Date(),
          archivedBy: userData?.uid
        }).filter(([_, value]) => value !== undefined)
      )
      
      await setDoc(archivedSpotRef, guestSpotDataToArchive)

      // Delete from the main collection
      const guestSpotRef = doc(db, LOCAL_GUEST_SPOTS_COLLECTION, guestSpot.id)
      await deleteDoc(guestSpotRef)

      // Update local state
      setGuestSpots(prevSpots => prevSpots.filter(spot => spot.id !== guestSpot.id))

      console.log('[GuestArtistsPage] Guest spot archived successfully:', guestSpot.id)
    } catch (error) {
      console.error('[GuestArtistsPage] Error archiving guest spot:', error)
      alert('Failed to archive guest spot. Please try again.')
    }
  }

  return (
    <div className="flex h-[100dvh] min-h-0 w-full max-w-full min-w-0 flex-col overflow-hidden overscroll-none bg-background md:h-[calc(100dvh-var(--desktop-top-nav-stack-height))]">
      <div className="sticky-header-notch-backdrop sticky top-0 z-40 shrink-0 border-b border-border/50 bg-background dark:border-border/30">
        <div className="phone-container-padded space-y-3 pb-3 pt-[var(--min-side-padding)] md:pt-0">
          <div className="relative overflow-hidden rounded-lg border border-border bg-card">
            <div className="relative h-40 w-full sm:h-44">
              <GuestArtistsMap
                guestSpots={guestSpots}
                artistsMap={artistsById}
                selectedCity={selectedCity}
                selectedCityCoordinates={selectedCityCoordinates}
              />

              <button
                onClick={() => setIsInfoModalOpen(true)}
                className="absolute right-2 top-2 z-20 flex touch-manipulation items-center gap-1.5 rounded-md border border-border bg-background/95 px-2.5 py-1.5 text-sm backdrop-blur-sm transition-colors hover:bg-accent"
              >
                <HelpCircle className="h-4 w-4" />
                <span>Info</span>
              </button>
            </div>
          </div>

          <CitySelector
            value={selectedCity}
            onChange={(cityName) => handleCityChange(cityName, true)}
            filterCities={cities}
            placeholder="Choose a city"
            className="w-full rounded-md bg-card"
            showLocationIcon
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="phone-container-padded pt-3 pb-bottom-nav md:pb-0">
          <h1 className="text-lg font-semibold text-foreground">
            {selectedCity ? `Upcoming Guest Spots in ${selectedCity}` : 'Upcoming Guest Spots'}
          </h1>

            {isAdmin && (
              <Button 
                onClick={handleCreateGuestSpot}
                className="mt-4 w-full"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Guest Spot
              </Button>
            )}

            <div className="mt-3">
            {isLoadingSpots ? (
              <div className="flex w-full min-w-0 flex-col gap-1 space-y-2">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="relative w-full min-w-0 overflow-hidden rounded-2xl border border-border/70 bg-card p-2 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-16 rounded-2xl border-4 border-border bg-muted animate-pulse md:h-20 md:w-20" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 w-32 rounded bg-muted animate-pulse" />
                        <div className="h-4 w-24 rounded bg-muted animate-pulse" />
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-1.5">
                      {[...Array(3)].map((_, imageIndex) => (
                        <div
                          key={imageIndex}
                          className="aspect-square rounded-xl bg-muted/60 animate-pulse"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : fetchError ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground">
                  Could not load guest artists for {selectedCity}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {fetchError.includes('index') || fetchError.includes('Index')
                    ? 'Firestore index is still building — try again in a minute.'
                    : fetchError}
                </p>
              </div>
            ) : guestSpots.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground">
                  No upcoming guest artists in {selectedCity}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Check back later or try another city
                </p>
              </div>
            ) : (
              <div className="flex w-full min-w-0 flex-col gap-1 space-y-2">
                {guestSpots.map((spot) => {
                  const platformArtist = resolvePlatformArtistForGuestSpot(
                    spot,
                    artistsById,
                    artistsByInstagramHandle,
                  )

                  return (
                    <GuestSpotCard
                      key={spot.id}
                      guestSpot={spot}
                      artist={platformArtist}
                      isAdmin={isAdmin}
                      onEdit={() => handleEditGuestSpot(spot)}
                      onDelete={() => handleDeleteGuestSpot(spot)}
                    />
                  )
                })}
              </div>
            )}
            </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingGuestSpot && (
        <GuestSpotEditModal
          guestSpot={editingGuestSpot}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setEditingGuestSpot(null)
          }}
          onSave={handleSaveGuestSpot}
        />
      )}

      {/* Create Modal */}
      <GuestSpotEditModal
        defaultTargetCity={selectedCity}
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateNewGuestSpot}
      />

      {/* Info Modal */}
      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  )
}
